import React, { useState, useEffect } from "react";
import { Modal, Button } from "antd";
import { motion } from "framer-motion";

// 🎁 Giveaway Modal Komponente
export const GiveawayModal = ({
  contest,
  visible,
  onClose,
  color = "#f97316",
  autoShow = true,
  form,
}) => {
  if (!contest) return null;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          style={{
            backgroundColor: color,
            borderColor: color,
          }}
          className="w-full"
        >
          Verstanden - Jetzt Feedback abgeben!
        </Button>,
      ]}
      width={650}
      className="custom-modal custom-scrollbar"
      styles={{
        body: {
          maxHeight: "80vh",
          overflowY: "scroll",
          padding: "20px 24px",
        },
      }}
      centered
      maskClosable={false}
    >
      <div className="text-center space-y-6 py-4">
        {/* Hero Section */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="text-6xl mb-4">🎁</div>
          <h2 className="text-3xl font-bold mb-2" style={{ color: color }}>
            Gewinnspiel läuft!
          </h2>
          <p className="text-lg text-gray-600">
            Geben Sie Ihr Feedback ab und nehmen Sie automatisch teil
          </p>
        </motion.div>

        {/* Contest Info */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-gray-50 p-6 rounded-xl"
        >
          <h3 className="text-xl font-bold mb-3">{contest.title}</h3>
          <p className="text-gray-700 mb-4">{contest.description}</p>

          {/* Zeitraum */}
          <div className="flex justify-center gap-6 text-sm mb-4">
            <div className="flex items-center gap-2">
              <span className="text-green-600">📅</span>
              <span className="font-medium">Start:</span>
              <span>
                {new Date(contest.startDate).toLocaleDateString("de-DE")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-600">⏰</span>
              <span className="font-medium">Ende:</span>
              <span>
                {new Date(contest.endDate).toLocaleDateString("de-DE")}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Preise */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <h4 className="text-lg font-bold mb-4 flex items-center justify-center gap-2">
            <span>🏆</span> Zu gewinnen:
          </h4>
          <div className="grid gap-3">
            {contest.prizes?.map((prize, index) => (
              <motion.div
                key={index}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                  style={{ backgroundColor: color }}
                >
                  {prize.place}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-semibold text-gray-800">
                    {prize.description}
                  </div>
                  <div className="text-sm text-gray-600">
                    Wert: {prize.value}
                  </div>
                </div>
                <div className="text-2xl">
                  {prize.place === 1 ? "🥇" : prize.place === 2 ? "🥈" : "🥉"}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Teilnahme Info */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="bg-green-50 p-4 rounded-lg border border-green-200"
        >
          <div className="flex items-center justify-center gap-2 text-green-800 font-bold mb-2">
            <span className="text-xl">✨</span>
            <span>Automatische Teilnahme!</span>
          </div>
          <p className="text-sm text-green-700">
            Sobald Sie Ihr Feedback abgeben, nehmen Sie automatisch am
            Gewinnspiel teil. Keine zusätzlichen Schritte erforderlich!
          </p>
        </motion.div>

        {/* Bedingungen */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="bg-blue-50 p-4 rounded-lg border border-blue-200"
        >
          <h5 className="font-semibold mb-2 text-blue-800 flex items-center justify-center gap-2">
            <span>ℹ️</span> Teilnahmebedingungen:
          </h5>
          <ul className="text-sm text-blue-700 space-y-1 text-left max-w-md mx-auto">
            <li className="flex items-start gap-2">
              <span className="text-green-600 mt-0.5">✓</span>
              <span>Alle Pflichtfragen müssen beantwortet werden</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 mt-0.5">✓</span>
              <span>Eine Teilnahme pro Person</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 mt-0.5">✓</span>
              <span>
                Gewinner werden nach Ende des Gewinnspiels benachrichtigt
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 mt-0.5">✓</span>
              <span>Anonyme Feedbacks nehmen nicht teil</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 mt-0.5">✓</span>
              <span>
                Die Gewinne werden direkt vom Unternehmen bereitgestellt. Reebaq
                übernimmt keine Gewährleistung oder Haftung für die Preise
              </span>
              <span>
                Die Gewinnspiel-Regularien entnehmen Sie den Gewinnspiel-AGBs
                des feedbacknehmenden Unternehmens
              </span>
            </li>
          </ul>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="text-center"
        >
          <div className="text-2xl mb-2">🍀</div>
          <p className="font-medium text-lg" style={{ color: color }}>
            Viel Glück bei der Teilnahme!
          </p>
        </motion.div>
      </div>
    </Modal>
  );
};
